/** QuickRouter — Propriétés de la sélection : composant (placement, références d'achat,
 *  broches et leurs nets), piste, via, texte ; sans sélection : la carte (gabarit, aspect,
 *  règles de conception, plans de cuivre, textes). */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import * as ops from '../../shared/ops.js';
import { polyBox, r4 } from '../../shared/geom.js';
import { padNetMap, netWidth } from '../../shared/netlist.js';
import NumField from './NumField.jsx';
import TextField from './TextField.jsx';
import LayersPanel from './LayersPanel.jsx';
import { copperLayers, layerRole, LAYER_LABELS } from '../../shared/layers.js';

const COLORS = ['vert', 'bleu', 'rouge', 'noir', 'blanc', 'jaune', 'violet'];
const RULES = [
  ['trackWidth', 'Piste signal'], ['powerWidth', 'Piste puissance'], ['clearance', 'Isolement'], ['edgeClearance', 'Marge au bord'],
  ['viaDrill', 'Perçage via'], ['viaDiameter', 'Ø via'], ['minDrill', 'Perçage mini'], ['grid', 'Grille'],
];

function ComponentProps({ comp }) {
  const update = useProjectStore((s) => s.updateComponent);
  const mutate = useProjectStore((s) => s.mutate);
  const project = useProjectStore((s) => s.project);
  const lookup = useProjectStore.getState().lookup();
  const fp = lookup(comp.footprint);
  const nets = padNetMap(project);
  const ro = project.role === 'viewer';
  const lockedMove = comp.locked || ro;
  const pads = fp ? [...new Map(fp.pads.map((p) => [p.num, p])).values()] : [];
  const setPinNet = (num, net) => mutate((p, lk) => {
    const key = `${comp.ref}.${num}`;
    if (!net) { for (const n of p.nets) n.pins = n.pins.filter((k) => k !== key); p.nets = p.nets.filter((n) => n.pins.length); return; }
    ops.setNet(p, lk, { name: net, pins: [key], mode: 'add' });
  });

  return (
    <div className="props">
      <div className="props-head"><span className="props-title">{comp.ref}</span><span className="muted">{fp?.name ?? comp.footprint}</span></div>
      {comp.locked && <div className="info-box">Connecteur imposé par le gabarit de carte : il reste à sa place.</div>}
      <div className="form-grid">
        <label>Repère</label><TextField disabled={ro} value={comp.ref} onCommit={(v) => v && update(comp.id, { ref: v.trim() })} />
        <label>Valeur</label><TextField disabled={ro} value={comp.value} onCommit={(v) => update(comp.id, { value: v })} />
        <label>X / Y (mm)</label>
        <div className="row">
          <NumField disabled={lockedMove} value={comp.pos[0]} onCommit={(v) => update(comp.id, { pos: [v, comp.pos[1]] })} />
          <NumField disabled={lockedMove} value={comp.pos[1]} onCommit={(v) => update(comp.id, { pos: [comp.pos[0], v] })} />
        </div>
        <label>Rotation</label><NumField disabled={lockedMove} value={comp.rot} onCommit={(v) => update(comp.id, { rot: v })} />
        <label>Face</label>
        <select disabled={lockedMove} value={comp.side} onChange={(e) => update(comp.id, { side: e.target.value })}>
          <option value="top">Dessus</option><option value="bottom">Dessous</option>
        </select>
        <label>Réf. fabricant</label><TextField disabled={ro} value={comp.mpn} onCommit={(v) => update(comp.id, { mpn: v })} />
        <label>Réf. LCSC</label><TextField disabled={ro} value={comp.lcsc} onCommit={(v) => update(comp.id, { lcsc: v.trim() })} placeholder="C1234" />
        <label>Non monté</label><input type="checkbox" disabled={ro} checked={comp.dnp} onChange={(e) => update(comp.id, { dnp: e.target.checked })} />
      </div>
      <label className="muted">Note</label>
      <TextField multiline rows={2} disabled={ro} value={comp.note} onCommit={(v) => update(comp.id, { note: v })} style={{ width: '100%' }} />
      <h3>Broches</h3>
      <datalist id="netnames">{project.nets.map((n) => <option key={n.id} value={n.name} />)}</datalist>
      <div className="pins">
        {pads.map((p) => (
          <div key={p.num} className="pin-row">
            <span className="mono pin-num">{p.num}</span>
            <span className="pin-name">{p.name}</span>
            <TextField disabled={ro} list="netnames" value={nets.get(`${comp.ref}.${p.num}`) ?? ''} placeholder="—" onCommit={(v) => setPinNet(p.num, v.trim())} />
          </div>
        ))}
      </div>
      {fp?.description && <><h3>Empreinte</h3><p className="desc">{fp.description}</p></>}
    </div>
  );
}

function TrackProps({ t }) {
  const mutate = useProjectStore((s) => s.mutate);
  const ro = useProjectStore((s) => s.project.role === 'viewer');
  const project = useProjectStore((s) => s.project);
  const len = t.pts.slice(1).reduce((s, q, i) => s + Math.hypot(q[0] - t.pts[i][0], q[1] - t.pts[i][1]), 0);
  return (
    <div className="props">
      <div className="props-head"><span className="props-title">Piste {t.net}</span><span className="muted">{t.auto ? 'routage automatique' : 'tracée à la main'}</span></div>
      <div className="form-grid">
        <label>Couche</label>
        <select disabled={ro} value={t.layer} onChange={(e) => mutate((p) => { p.tracks.find((x) => x.id === t.id).layer = e.target.value; })}>
          {copperLayers(project.board).map((L) => <option key={L} value={L}>{LAYER_LABELS[L]}</option>)}
        </select>
        <label>Largeur (mm)</label><NumField disabled={ro} value={t.width} onCommit={(v) => v > 0 && mutate((p) => { p.tracks.find((x) => x.id === t.id).width = v; })} />
        <label>Longueur</label><span>{r4(len)} mm · {t.pts.length - 1} segment(s)</span>
      </div>
      <div className="row"><button disabled={ro} onClick={() => mutate((p) => { p.tracks.find((x) => x.id === t.id).width = netWidth(project, t.net, t.layer); })}>Largeur du net</button></div>
    </div>
  );
}

function BoardProps() {
  const project = useProjectStore((s) => s.project);
  const library = useProjectStore((s) => s.library);
  const mutate = useProjectStore((s) => s.mutate);
  const ro = project.role === 'viewer';
  const [tpl, setTpl] = useState(null);
  const [x0, y0, x1, y1] = polyBox(project.board.outline);
  const b = project.board;
  const template = library.templates.find((t) => t.id === b.template);
  const gnd = project.nets.find((n) => /^gnd$/i.test(n.name));

  return (
    <div className="props">
      <div className="props-head"><span className="props-title">Carte</span><span className="muted">{r4(x1 - x0)} × {r4(y1 - y0)} mm</span></div>
      <div className="form-grid">
        <label>Gabarit</label>
        <div className="row">
          <span className="grow">{template?.name ?? b.template}</span>
          <button disabled={ro} onClick={() => setTpl({ id: b.template, params: Object.fromEntries((template?.params ?? []).map((p) => [p.id, p.default])) })}>Changer…</button>
        </div>
        <label>Épaisseur</label>
        <select disabled={ro} value={b.thickness} onChange={(e) => mutate((p) => { p.board.thickness = Number(e.target.value); })}>
          {[0.8, 1.0, 1.2, 1.6, 2.0].map((v) => <option key={v} value={v}>{v} mm</option>)}
        </select>
        <label>Vernis</label>
        <select disabled={ro} value={b.color} onChange={(e) => mutate((p) => { p.board.color = e.target.value; })}>
          {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label>Finition</label>
        <select disabled={ro} value={b.finish} onChange={(e) => mutate((p) => { p.board.finish = e.target.value; })}>
          {['HASL', 'HASL sans plomb', 'ENIG'].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {tpl && (
        <div className="tpl-box">
          <select value={tpl.id} onChange={(e) => { const t = library.templates.find((x) => x.id === e.target.value); setTpl({ id: t.id, params: Object.fromEntries(t.params.map((p) => [p.id, p.default])) }); }}>
            {library.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <p className="desc">{library.templates.find((t) => t.id === tpl.id)?.description}</p>
          {library.templates.find((t) => t.id === tpl.id)?.params.map((p) => (
            <div key={p.id} className="row"><label className="grow muted">{p.label}</label><NumField style={{ width: 70 }} value={tpl.params[p.id]} onCommit={(v) => setTpl({ ...tpl, params: { ...tpl.params, [p.id]: v } })} /></div>
          ))}
          <div className="warn small">Le contour et les connecteurs imposés sont remplacés ; vos composants restent en place.</div>
          <div className="row"><button onClick={() => setTpl(null)}>Annuler</button><button className="primary" onClick={() => { mutate((p, lk) => ops.applyTemplate(p, tpl.id, tpl.params, lk)); setTpl(null); useProjectStore.getState().fit(); }}>Appliquer</button></div>
        </div>
      )}
      <LayersPanel />
      <h3>Règles de conception (mm)</h3>
      <div className="form-grid">
        {RULES.map(([k, label]) => (
          <FragmentRule key={k} label={label} value={project.rules[k]} disabled={ro} onCommit={(v) => v > 0 && mutate((p) => { p.rules[k] = v; })} />
        ))}
      </div>
      <h3>Plans de cuivre</h3>
      {project.zones.map((z) => (
        <div key={z.id} className="row">
          <span className="grow">{z.net} — {LAYER_LABELS[z.layer] ?? z.layer}{z.outline ? ' (contour propre)' : ' (toute la carte)'}</span>
          <button className="icon" disabled={ro} title="Supprimer" onClick={() => mutate((p) => ops.deleteItems(p, [z.id]))}>✕</button>
        </div>
      ))}
      {!ro && (
        <div className="row wrap">
          {gnd ? (
            <>
              {copperLayers(project.board).filter((L) => L === 'F' || L === 'B' || layerRole(project.board, L) === 'plane').map((L) => (
                <button key={L} onClick={() => mutate((p) => ops.upsertZone(p, { net: gnd.name, layer: L }))}>+ {gnd.name} {L === 'F' ? 'dessus' : L === 'B' ? 'dessous' : `sur ${L} (plan)`}</button>
              ))}
            </>
          ) : <span className="muted">Créez un net GND pour poser un plan de masse.</span>}
        </div>
      )}
      <h3>Textes de sérigraphie</h3>
      {project.texts.map((t) => (
        <div key={t.id} className="row">
          <TextField className="grow" disabled={ro} value={t.text} onCommit={(v) => v && mutate((p) => { p.texts.find((x) => x.id === t.id).text = v; })} />
          <button className="icon" disabled={ro} onClick={() => mutate((p) => ops.deleteItems(p, [t.id]))}>✕</button>
        </div>
      ))}
      {!ro && <button onClick={() => mutate((p) => ops.addText(p, { text: project.name.slice(0, 40), pos: [r4((x0 + x1) / 2), r4(y0 + 3)] }))}>+ Texte</button>}
    </div>
  );
}

function FragmentRule({ label, value, onCommit, disabled }) {
  return (<><label>{label}</label><NumField disabled={disabled} value={value} onCommit={onCommit} /></>);
}

export default function PropsPanel() {
  const project = useProjectStore((s) => s.project);
  const selection = useProjectStore((s) => s.selection);
  const del = useProjectStore((s) => s.deleteSelection);
  if (!selection.length) return <BoardProps />;
  if (selection.length > 1) {
    return (
      <div className="props">
        <div className="props-head"><span className="props-title">{selection.length} éléments</span></div>
        <div className="row"><button className="danger" onClick={del}>Supprimer</button></div>
      </div>
    );
  }
  const id = selection[0];
  const comp = project.components.find((c) => c.id === id);
  if (comp) return <ComponentProps comp={comp} />;
  const t = project.tracks.find((x) => x.id === id);
  if (t) return <TrackProps t={t} />;
  const v = project.vias.find((x) => x.id === id);
  if (v) return <div className="props"><div className="props-head"><span className="props-title">Via {v.net}</span></div><p className="muted">Perçage {v.drill} mm, Ø {v.diameter} mm, en ({v.pos.join(' ; ')}).</p></div>;
  return <BoardProps />;
}
